import React, { useEffect, useState } from 'react';
import Link from './Link';
import LineChart from './LineChart';
import { ReactComponent as ArrowLeft } from 'assets/img/arrow-left.svg';
import ChartLegend from './ChartLegend';

export default function StateSidePanel({
  children,
  socioeconomicYearsData = null,
  features = null,
  socioeconomicFactor = null,
  backLinkAction,
  activeState,
}) {
  const [dataChart1, setDataChart1] = useState(null);
  const [dataChart2, setDataChart2] = useState(null);
  const [previousActiveState, setPreviousActiveState] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);

  useEffect(() => {
    if (!socioeconomicYearsData) {
      return;
    }

    if (activeState?.statename === previousActiveState?.statename) {
      return;
    }

    const broadbandYearsChartData = socioeconomicYearsData.map((item) => item.broadband);
    const socioeconomicYearsChartLabels = socioeconomicYearsData.map((item) => item.year);

    setDataChart1({
      labels: socioeconomicYearsChartLabels,
      datasets: [
        {
          data: broadbandYearsChartData,
          borderColor: function (context) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return;
            }

            return getGradientBlue(ctx, chartArea);
          },
          fill: false,
          tension: 0.5,
        },
      ],
    });

    setPreviousActiveState(activeState);
  }, [activeState, previousActiveState?.statename, socioeconomicYearsData]);

  useEffect(() => {
    if (!socioeconomicYearsData || !activeState?.statename) {
      return;
    }

    if (
      socioeconomicFactor === previousSocioeconomicFactor &&
      activeState?.statename === previousActiveState?.statename
    ) {
      return;
    }

    if (socioeconomicYearsData?.[0][socioeconomicFactor] === undefined) {
      return;
    }

    const socioeconomicYearsChartData = socioeconomicYearsData.map(
      (item) => item[socioeconomicFactor]
    );
    const socioeconomicYearsChartLabels = socioeconomicYearsData.map((item) => item.year);

    setDataChart2({
      labels: socioeconomicYearsChartLabels,
      datasets: [
        {
          data: socioeconomicYearsChartData,
          borderColor: function (context) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return;
            }

            return getGradientBlue(ctx, chartArea);
          },
          fill: false,
          tension: 0.5,
        },
      ],
    });

    setPreviousActiveState(activeState);
    setPreviousSocioeconomicFactor(socioeconomicFactor);
  }, [
    socioeconomicYearsData,
    socioeconomicFactor,
    previousActiveState,
    activeState,
    previousSocioeconomicFactor,
  ]);

  const getGradientBlue = function (ctx, chartArea) {
    let gradient;

    gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    gradient.addColorStop(0, '#F3F5F7');
    gradient.addColorStop(0.1, '#D6DFF4');
    gradient.addColorStop(0.5, '#142C60');
    gradient.addColorStop(1, '#142C60');

    return gradient;
  };

  return (
    <div data-component-name='StateSidePanel' className='h-full'>
      <div className='flex flex-col h-full text-blue-600'>
        {backLinkAction && (
          <Link
            action={backLinkAction}
            icon={<ArrowLeft />}
            isIconLeft={true}
            color='text-blue-500/60'
          >
            <span className='text-base -translate-y-px'>back</span>
          </Link>
        )}

        <h2 className='pb-4 mt-4 text-lg font-bold'>Visualizations</h2>

        <div className='h-full overflow-auto pb-7'>
          <h3 className='mt-3 text-sm font-semibold'>Broadband score over the years</h3>
          <div className='mt-3 '>
            <LineChart
              data={dataChart1}
              labelY={<span>Broadband score</span>}
              labelX={<span className='pl-3'>Years</span>}
            >
              <ChartLegend items={[{ value: 'blue', label: 'Observation' }]} />
            </LineChart>
          </div>

          <h3 className='text-sm font-semibold mt-7'>
            {findFeatureLabel(features, socioeconomicFactor)} over the years
          </h3>
          <div className='mt-3 '>
            <LineChart
              data={dataChart2}
              labelY={<span>{findFeatureLabel(features, socioeconomicFactor)}</span>}
              labelX={<span className='pl-3'>Years</span>}
            >
              <ChartLegend items={[{ value: 'blue', label: 'Observation' }]} />
            </LineChart>
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}

const findFeatureLabel = function (features, input) {
  if (features === null) {
    return;
  }

  const categoryMatch =
    features.find((categoryData) => categoryData.category === input) || null;

  if (categoryMatch !== null) {
    return categoryMatch.category;
  }

  const featureMatch = features
    .flatMap((categoryData) => categoryData.features)
    .find((featureData) => featureData.id === input);

  return featureMatch ? featureMatch.label : null;
};
